import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { ChildCareFlags } from '../components/ChildCareFlags';
import { senCategoryLabel, senStatusLabel, normalizeSpecialNeeds } from '../utils/childFields';
import { ageAndYearLabel, ukYearGroup } from '../utils/childAge';
import '../App.css';

const ChildProfile = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [milestones, setMilestones] = useState([]);
  const [reports, setReports] = useState([]);
  const [immunizations, setImmunizations] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [childId, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const childData = await api.getChild(childId);
      setChild(childData);

      if (activeTab === 'milestones') {
        const milestonesData = await api.getMilestones({ child_id: childId });
        setMilestones(milestonesData);
      } else if (activeTab === 'reports') {
        const reportsData = await api.getLearningReports({ child_id: childId });
        setReports(reportsData);
      } else if (activeTab === 'immunizations') {
        const immunizationsData = await api.getImmunizations({ child_id: childId });
        setImmunizations(immunizationsData);
      } else if (activeTab === 'incidents') {
        const incidentsData = await api.getIncidents({ child_id: childId });
        setIncidents(incidentsData);
      } else if (activeTab === 'messages') {
        const communicationsData = await api.getCommunications({ child_id: childId });
        setCommunications(communicationsData);
      } else if (activeTab === 'moments') {
        const momentsData = await api.getMoments({ child_id: childId });
        setMoments(momentsData);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  if (loading && !child) return <div className="loading">Loading child profile...</div>;
  if (!child) return <div className="error">Child not found</div>;

  const getCategoryIcon = (category) => {
    const icons = {
      physical: '🏃',
      cognitive: '🧠',
      social: '👥',
      emotional: '❤️',
      language: '💬'
    };
    return icons[category] || '📝';
  };

  const getStatusBadge = (status) => {
    const badges = {
      not_started: 'badge-gray',
      in_progress: 'badge-yellow',
      achieved: 'badge-green'
    };
    return badges[status] || 'badge-gray';
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <button className="btn btn-ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          ← Back
        </button>
        <h1>{child.first_name} {child.last_name}</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {(() => {
            const yg = ukYearGroup(child.date_of_birth);
            return yg ? (
              <>
                <span className="badge badge-blue" data-testid="profile-year-group">{yg.label}</span>
                {yg.key_stage && yg.key_stage !== '—' && (
                  <span className="badge" style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--foreground))' }}>{yg.key_stage}</span>
                )}
              </>
            ) : (
              <span className="badge badge-blue">{child.age_group}</span>
            );
          })()}
          <span style={{ color: 'hsl(var(--muted-foreground))' }} data-testid="profile-age-label">
            {ageAndYearLabel(child.date_of_birth) || `Age: ${child.age || 'N/A'}`}
          </span>
          {/* Care flags surfaced right next to the badge so staff/parents
              can see SEND, dietary and allergy info at a single glance. */}
          <ChildCareFlags child={child} testIdPrefix="profile-header-" />
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs" style={{ marginBottom: '2rem', borderBottom: '1px solid hsl(var(--border))' }}>
        {['overview', 'milestones', 'reports', 'immunizations', 'incidents', 'messages', 'moments'].map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === tab ? 'hsl(var(--primary))' : 'transparent',
              color: activeTab === tab ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
              border: 'none',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              textTransform: 'capitalize'
            }}
            data-testid={`tab-${tab}`}
          >
            {tab === 'overview' && '📋'} {tab === 'milestones' && '🎯'} 
            {tab === 'reports' && '📚'} {tab === 'immunizations' && '💉'}
            {tab === 'incidents' && '🚑'} {tab === 'messages' && '💬'}
            {tab === 'moments' && '📸'} {tab.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="card-grid">
          <div className="card">
            <h3>Basic Information</h3>
            <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
              <div><strong>Full Name:</strong> {child.first_name} {child.last_name}</div>
              <div><strong>Age Group:</strong> {child.age_group}</div>
              {(() => {
                const yg = ukYearGroup(child.date_of_birth);
                return yg ? (
                  <div data-testid="profile-yeargroup-row">
                    <strong>Year group:</strong> {yg.label}
                    {yg.key_stage && yg.key_stage !== '—' && <> ({yg.key_stage})</>}
                  </div>
                ) : null;
              })()}
              {child.date_of_birth && <div><strong>Date of Birth:</strong> {new Date(child.date_of_birth).toLocaleDateString()} ({ageAndYearLabel(child.date_of_birth)})</div>}
              {child.medical_notes && <div><strong>Medical Notes:</strong> {child.medical_notes}</div>}
              {child.allergies && child.allergies.length > 0 && (
                <div>
                  <strong>Allergies:</strong>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    {child.allergies.map((allergy, i) => (
                      <span key={i} className="badge badge-red">{allergy}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* School information card — visible whenever the child has
              any school details on file (regardless of age group). */}
          {child.school_info?.school_name && (
            <div className="card" data-testid="profile-school-card">
              <h3>🏫 School</h3>
              <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
                <div><strong>School:</strong> {child.school_info.school_name}</div>
                {child.school_info.school_class && (
                  <div><strong>Class / year:</strong> {child.school_info.school_class}</div>
                )}
                {child.school_info.teacher_name && (
                  <div><strong>Teacher:</strong> {child.school_info.teacher_name}</div>
                )}
                {child.school_info.school_address && (
                  <div><strong>Address:</strong> {[child.school_info.school_address, child.school_info.school_postcode].filter(Boolean).join(', ')}</div>
                )}
                {child.school_info.school_meeting_point && (
                  <div><strong>🚌 Collection location:</strong> {child.school_info.school_meeting_point}</div>
                )}
                {child.school_info.school_contact_number && (
                  <div><strong>School phone:</strong> <a href={`tel:${child.school_info.school_contact_number}`}>{child.school_info.school_contact_number}</a></div>
                )}
                {child.school_info.school_notes && (
                  <div><em>{child.school_info.school_notes}</em></div>
                )}
              </div>
            </div>
          )}

          {/* Care Needs — dedicated card for SEND + dietary so staff can find
              this critical info quickly. Only shown when at least one signal
              exists, so default profiles stay clean. */}
          {(child.dietary_requirements?.length > 0 || child.special_needs?.has_sen) && (
            <div className="card" data-testid="care-needs-card">
              <h3>Care Needs</h3>
              <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                {child.dietary_requirements?.length > 0 && (
                  <div data-testid="care-needs-dietary">
                    <strong>Dietary requirements:</strong>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                      {child.dietary_requirements.map((d, i) => (
                        <span
                          key={i}
                          style={{
                            padding: '0.25rem 0.65rem',
                            borderRadius: '999px',
                            background: 'hsl(210 90% 95%)',
                            color: 'hsl(215 70% 32%)',
                            border: '1px solid hsl(210 75% 85%)',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                          }}
                        >
                          🍽 {d}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {child.special_needs?.has_sen && (() => {
                  const sn = normalizeSpecialNeeds(child.special_needs);
                  return (
                    <div data-testid="care-needs-sen">
                      <strong>Special Educational Needs (SEND):</strong>
                      <div style={{ marginTop: '0.4rem', fontSize: '0.875rem', display: 'grid', gap: '0.45rem' }}>
                        <div>
                          <em style={{ color: 'hsl(var(--muted-foreground))' }}>Status:</em>{' '}
                          <span style={{ fontWeight: 600 }}>{senStatusLabel(sn.sen_status)}</span>
                        </div>
                        {sn.categories.length > 0 && (
                          <div>
                            <em style={{ color: 'hsl(var(--muted-foreground))' }}>Categories:</em>
                            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
                              {sn.categories.map((c, i) => (
                                <span
                                  key={i}
                                  style={{
                                    padding: '0.2rem 0.55rem',
                                    borderRadius: '999px',
                                    background: 'hsl(35 90% 94%)',
                                    color: 'hsl(30 80% 30%)',
                                    border: '1px solid hsl(35 80% 80%)',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {senCategoryLabel(c)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {sn.ehcp_review_date && (
                          <div>
                            <em style={{ color: 'hsl(var(--muted-foreground))' }}>EHCP review:</em>{' '}
                            {new Date(sn.ehcp_review_date).toLocaleDateString()}
                          </div>
                        )}
                        {sn.notes && (
                          <div>
                            <em style={{ color: 'hsl(var(--muted-foreground))' }}>Notes:</em>{' '}
                            <span>{sn.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="card">
            <h3>Quick Stats</h3>
            <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Milestones Achieved:</span>
                <strong>{milestones.filter(m => m.status === 'achieved').length}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Learning Reports:</span>
                <strong>{reports.length}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Immunizations:</span>
                <strong>{immunizations.filter(i => i.status === 'completed').length}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Moments:</span>
                <strong>{moments.length}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestones Tab */}
      {activeTab === 'milestones' && (
        <div>
          {loading ? (
            <div>Loading milestones...</div>
          ) : milestones.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {['physical', 'cognitive', 'social', 'emotional', 'language'].map(category => {
                const categoryMilestones = milestones.filter(m => m.category === category);
                if (categoryMilestones.length === 0) return null;

                return (
                  <div key={category} className="card">
                    <h3>{getCategoryIcon(category)} {category.charAt(0).toUpperCase() + category.slice(1)} Development</h3>
                    <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
                      {categoryMilestones.map(milestone => (
                        <div key={milestone._id} style={{ 
                          padding: '1rem', 
                          background: 'hsl(var(--muted))', 
                          borderRadius: '6px',
                          borderLeft: `4px solid ${milestone.status === 'achieved' ? '#22c55e' : milestone.status === 'in_progress' ? '#f59e0b' : '#94a3b8'}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <strong>{milestone.title}</strong>
                              <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                                {milestone.age_range}
                              </div>
                              {milestone.description && (
                                <p style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>{milestone.description}</p>
                              )}
                              {milestone.staff_notes && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                  Note: {milestone.staff_notes}
                                </div>
                              )}
                            </div>
                            <span className={`badge ${getStatusBadge(milestone.status)}`}>
                              {milestone.status.replace('_', ' ')}
                            </span>
                          </div>
                          {milestone.achieved_date && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#22c55e' }}>
                              ✅ Achieved: {new Date(milestone.achieved_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>No milestones tracked yet</p>
            </div>
          )}
        </div>
      )}

      {/* Learning Reports Tab */}
      {activeTab === 'reports' && (
        <div>
          {loading ? (
            <div>Loading reports...</div>
          ) : reports.length > 0 ? (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {reports.map(report => (
                <div key={report._id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h3>{report.period.toUpperCase()} Report</h3>
                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                        {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
                      </div>
                      {report.created_by_name && (
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          By: {report.created_by_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {report.areas && report.areas.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4>Learning Areas</h4>
                      <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                        {report.areas.map((area, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{area.area.replace('_', ' ')}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {[1, 2, 3, 4, 5].map(star => (
                                  <span key={star} style={{ color: star <= (area.rating || 0) ? '#FFB84D' : '#d1d5db' }}>★</span>
                                ))}
                              </div>
                              {area.notes && <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>• {area.notes}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {report.strengths && report.strengths.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4>✨ Strengths</h4>
                      <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                        {report.strengths.map((strength, i) => (
                          <li key={i}>{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {report.areas_for_development && report.areas_for_development.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4>🎯 Areas for Development</h4>
                      <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                        {report.areas_for_development.map((area, i) => (
                          <li key={i}>{area}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {report.overall_notes && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'hsl(var(--muted))', borderRadius: '6px' }}>
                      <strong>Overall Notes:</strong>
                      <p style={{ marginTop: '0.5rem' }}>{report.overall_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No learning reports yet</p>
            </div>
          )}
        </div>
      )}

      {/* Immunizations Tab */}
      {activeTab === 'immunizations' && (
        <div>
          {loading ? (
            <div>Loading immunizations...</div>
          ) : immunizations.length > 0 ? (
            <div className="card">
              <h3>Immunization Record</h3>
              <table style={{ marginTop: '1rem' }}>
                <thead>
                  <tr>
                    <th>Vaccine</th>
                    <th>Dose</th>
                    <th>Date Given</th>
                    <th>Next Due</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {immunizations.map(imm => (
                    <tr key={imm._id}>
                      <td><strong>{imm.vaccine_name}</strong></td>
                      <td>{imm.dose_number ? `Dose ${imm.dose_number}` : 'Single'}</td>
                      <td>{imm.date_given ? new Date(imm.date_given).toLocaleDateString() : 'Not given'}</td>
                      <td>{imm.next_due_date ? new Date(imm.next_due_date).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <span className={`badge ${
                          imm.status === 'completed' ? 'badge-green' :
                          imm.status === 'scheduled' ? 'badge-blue' :
                          imm.status === 'overdue' ? 'badge-red' :
                          'badge-gray'
                        }`}>
                          {imm.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No immunization records</p>
            </div>
          )}
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && (
        <div>
          {loading ? (
            <div>Loading incidents...</div>
          ) : incidents.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {incidents.map(incident => (
                <div key={incident._id} className="card" style={{
                  borderLeft: `4px solid ${
                    incident.severity === 'emergency' ? '#ef4444' :
                    incident.severity === 'serious' ? '#f59e0b' :
                    incident.severity === 'moderate' ? '#eab308' :
                    '#94a3b8'
                  }`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3>{incident.incident_type.replace('_', ' ').toUpperCase()}</h3>
                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                        {new Date(incident.incident_date).toLocaleString()} • {incident.location}
                      </div>
                    </div>
                    <span className={`badge ${
                      incident.severity === 'emergency' ? 'badge-red' :
                      incident.severity === 'serious' ? 'badge-red' :
                      incident.severity === 'moderate' ? 'badge-yellow' :
                      'badge-gray'
                    }`}>
                      {incident.severity}
                    </span>
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <strong>Description:</strong>
                    <p style={{ marginTop: '0.5rem' }}>{incident.description}</p>
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <strong>Action Taken:</strong>
                    <p style={{ marginTop: '0.5rem' }}>{incident.action_taken}</p>
                  </div>

                  {incident.reported_by_name && (
                    <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                      Reported by: {incident.reported_by_name}
                    </div>
                  )}

                  {incident.parent_notified && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'hsl(142 76% 95%)', borderRadius: '6px', color: 'hsl(142 71% 25%)' }}>
                      ✅ Parent notified on {new Date(incident.parent_notified_at).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No incidents reported</p>
            </div>
          )}
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div>
          {loading ? (
            <div>Loading messages...</div>
          ) : communications.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {communications.map(comm => (
                <div key={comm._id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <h4 style={{ margin: 0 }}>{comm.subject}</h4>
                        <span className={`badge ${
                          comm.priority === 'urgent' ? 'badge-red' :
                          comm.priority === 'high' ? 'badge-yellow' :
                          'badge-gray'
                        }`}>
                          {comm.message_type.replace('_', ' ')}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                        From: {comm.sent_by_name} • {new Date(comm.created_at).toLocaleString()}
                      </div>
                      <p>{comm.message}</p>
                    </div>
                    {!comm.is_read && (
                      <span className="badge badge-blue">New</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No messages</p>
            </div>
          )}
        </div>
      )}

      {/* Moments Tab */}
      {activeTab === 'moments' && (
        <div>
          {loading ? (
            <div>Loading moments...</div>
          ) : moments.length > 0 ? (
            <div className="card-grid">
              {moments.map(moment => (
                <div key={moment._id} className="card">
                  {moment.photo_url && (
                    <img 
                      src={moment.photo_url} 
                      alt={moment.caption} 
                      style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '6px', marginBottom: '1rem' }}
                    />
                  )}
                  <p>{moment.caption}</p>
                  <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                    {new Date(moment.created_at).toLocaleDateString()}
                  </div>
                  {moment.tags && moment.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                      {moment.tags.map((tag, i) => (
                        <span key={i} className="badge badge-blue" style={{ fontSize: '0.75rem' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No moments yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChildProfile;
